import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Ticket, Plus, Search, Filter, AlertTriangle, Clock, CheckCircle2,
  MessageSquare, ChevronRight, X, Send, Shield,
} from 'lucide-react';
import {
  supportApi,
  type Ticket as TicketType,
  type TicketStatus,
  type TicketPriority,
  type TicketCategory,
  type Comment,
  type ListTicketsParams,
} from '../../../api/support';

// ─── Status / Priority helpers ──────────────────────────────────────────────

const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_on_customer: 'Waiting',
  resolved: 'Resolved',
  closed: 'Closed',
};

const STATUS_COLORS: Record<TicketStatus, string> = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  waiting_on_customer: 'bg-purple-100 text-purple-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-neutral-100 text-neutral-500',
};

const PRIORITY_COLORS: Record<TicketPriority, string> = {
  low: 'bg-neutral-100 text-neutral-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

const STATUS_TRANSITIONS: Record<TicketStatus, TicketStatus[]> = {
  open: ['in_progress'],
  in_progress: ['waiting_on_customer', 'resolved'],
  waiting_on_customer: ['in_progress'],
  resolved: ['closed', 'open'],
  closed: ['open'],
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

function useTickets(params: ListTicketsParams) {
  return useQuery({
    queryKey: ['tickets', params],
    queryFn: () => supportApi.listTickets(params),
    staleTime: 30_000,
  });
}

function useTicket(id: string | null) {
  return useQuery({
    queryKey: ['ticket', id],
    queryFn: () => supportApi.getTicket(id!),
    enabled: !!id,
  });
}

function useComments(ticketId: string | null) {
  return useQuery({
    queryKey: ['ticket-comments', ticketId],
    queryFn: () => supportApi.listComments(ticketId!),
    enabled: !!ticketId,
  });
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: TicketStatus }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: TicketPriority }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[priority]}`}>
      {priority}
    </span>
  );
}

function SLABadge({ ticket }: { ticket: TicketType }) {
  if (!ticket.sla_status) return null;
  const { breached, time_remaining_minutes } = ticket.sla_status;
  if (breached) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <AlertTriangle size={11} /> SLA Breached
      </span>
    );
  }
  if (time_remaining_minutes !== null && time_remaining_minutes < 60) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <Clock size={11} /> {time_remaining_minutes}m left
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <Shield size={11} /> On track
    </span>
  );
}

// ─── Create Ticket Modal ─────────────────────────────────────────────────────

function CreateTicketModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<TicketCategory>('technical');
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await supportApi.createTicket({ subject, description, category, priority });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
          <h2 className="text-base font-semibold text-neutral-900">New Ticket</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Subject</label>
            <input
              value={subject} onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Brief summary of the issue"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Description</label>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:ring-1 focus:ring-indigo-500 focus:border-transparent h-24 resize-none"
              placeholder="Detailed description..."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value as TicketCategory)}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white">
                <option value="technical">Technical</option>
                <option value="billing">Billing</option>
                <option value="account">Account</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value as TicketPriority)}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-neutral-600 border border-neutral-200 rounded-lg hover:bg-neutral-50">Cancel</button>
            <button type="submit" disabled={submitting}
              className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              {submitting ? 'Creating...' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Ticket Detail Panel ─────────────────────────────────────────────────────

function TicketDetail({ ticketId, onClose }: { ticketId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: ticket, isLoading } = useTicket(ticketId);
  const { data: commentsData } = useComments(ticketId);
  const [commentText, setCommentText] = useState('');

  const transitionMut = useMutation({
    mutationFn: ({ status }: { status: TicketStatus }) =>
      supportApi.transitionStatus(ticketId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
  });

  const commentMut = useMutation({
    mutationFn: (body: string) => supportApi.addComment(ticketId, body),
    onSuccess: () => {
      setCommentText('');
      queryClient.invalidateQueries({ queryKey: ['ticket-comments', ticketId] });
    },
  });

  if (isLoading || !ticket) {
    return (
      <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-xl z-40 border-l border-neutral-100">
        <div className="p-6">
          <div className="h-6 w-48 bg-neutral-100 rounded animate-pulse mb-4" />
          <div className="h-4 w-full bg-neutral-100 rounded animate-pulse mb-2" />
          <div className="h-4 w-3/4 bg-neutral-100 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const transitions = STATUS_TRANSITIONS[ticket.status] ?? [];
  const comments = commentsData?.comments ?? [];

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-xl z-40 border-l border-neutral-100 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 shrink-0">
        <div className="flex items-center gap-3">
          <StatusBadge status={ticket.status} />
          <PriorityBadge priority={ticket.priority} />
          <SLABadge ticket={ticket} />
        </div>
        <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600"><X size={18} /></button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900 mb-1">{ticket.subject}</h2>
          <p className="text-sm text-neutral-500">
            Created {new Date(ticket.created_at).toLocaleDateString()} · #{ticket.id.slice(0, 8)}
          </p>
        </div>

        {ticket.description && (
          <div className="bg-neutral-50 rounded-lg p-4 text-sm text-neutral-700 whitespace-pre-wrap">
            {ticket.description}
          </div>
        )}

        {/* Status transitions */}
        {transitions.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-neutral-500">Move to:</span>
            {transitions.map((s) => (
              <button
                key={s}
                onClick={() => transitionMut.mutate({ status: s })}
                disabled={transitionMut.isPending}
                className="px-3 py-1 text-xs font-medium border border-neutral-200 rounded-lg hover:bg-neutral-50 text-neutral-700"
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        )}

        {/* Comments */}
        <div>
          <h3 className="text-sm font-semibold text-neutral-800 mb-3 flex items-center gap-1.5">
            <MessageSquare size={14} /> Comments ({comments.length})
          </h3>
          <div className="space-y-3">
            {comments.map((c: Comment) => (
              <div key={c.id} className={`rounded-lg p-3 text-sm ${c.is_internal ? 'bg-amber-50 border border-amber-100' : 'bg-neutral-50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-neutral-700">{c.author_type === 'agent' ? 'Agent' : 'Customer'}</span>
                  <span className="text-xs text-neutral-400">{new Date(c.created_at).toLocaleString()}</span>
                  {c.is_internal && <span className="text-xs text-amber-600 font-medium">Internal</span>}
                </div>
                <p className="text-neutral-600 whitespace-pre-wrap">{c.body}</p>
              </div>
            ))}
            {comments.length === 0 && (
              <p className="text-sm text-neutral-400 text-center py-4">No comments yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Comment input */}
      <div className="border-t border-neutral-100 p-4 shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add a comment..."
            className="flex-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm resize-none h-16 focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
          />
          <button
            onClick={() => commentText.trim() && commentMut.mutate(commentText.trim())}
            disabled={!commentText.trim() || commentMut.isPending}
            className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function SupportTicketsPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const params: ListTicketsParams = {};
  if (statusFilter) params.status = statusFilter;
  if (priorityFilter) params.priority = priorityFilter;

  const { data, isLoading, isError } = useTickets(params);
  const tickets = data?.tickets ?? [];

  const filtered = search
    ? tickets.filter((t) => t.subject.toLowerCase().includes(search.toLowerCase()))
    : tickets;

  // Trend stats
  const open = tickets.filter((t) => t.status === 'open').length;
  const inProgress = tickets.filter((t) => t.status === 'in_progress').length;
  const resolved = tickets.filter((t) => t.status === 'resolved').length;
  const breached = tickets.filter((t) => t.sla_breached).length;

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Ticket size={20} className="text-indigo-600" />
              <h1 className="text-xl font-semibold text-neutral-900">Support Tickets</h1>
            </div>
            <p className="text-sm text-neutral-500">Manage and resolve customer support requests</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            <Plus size={15} /> New Ticket
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Open', value: open, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'In Progress', value: inProgress, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Resolved', value: resolved, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'SLA Breached', value: breached, color: 'text-red-600', bg: 'bg-red-50' },
          ].map((s) => (
            <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
              <p className="text-2xl font-semibold mb-0.5"><span className={s.color}>{s.value}</span></p>
              <p className="text-xs text-neutral-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tickets..."
              className="w-full pl-9 pr-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <Filter size={14} className="text-neutral-400" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as TicketStatus | '')}
              className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white text-neutral-700">
              <option value="">All statuses</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value as TicketPriority | '')}
              className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white text-neutral-700">
              <option value="">All priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        {/* Ticket list */}
        <div className="bg-white border border-neutral-100 rounded-xl shadow-sm">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="h-4 w-16 bg-neutral-100 rounded animate-pulse" />
                  <div className="h-4 flex-1 bg-neutral-100 rounded animate-pulse" />
                  <div className="h-4 w-20 bg-neutral-100 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="p-10 text-center text-sm text-red-600">
              Failed to load tickets. Check that the support service is running.
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-neutral-400">
              {search ? 'No tickets match your search' : 'No tickets found'}
            </div>
          ) : (
            <div className="divide-y divide-neutral-50">
              {filtered.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  className="w-full flex items-center gap-4 px-5 py-3 hover:bg-neutral-50 text-left transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate">{t.subject}</p>
                    <p className="text-xs text-neutral-400 mt-0.5">
                      {t.category} · {new Date(t.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <StatusBadge status={t.status} />
                  <PriorityBadge priority={t.priority} />
                  {t.sla_breached && (
                    <AlertTriangle size={14} className="text-red-500 shrink-0" />
                  )}
                  <ChevronRight size={16} className="text-neutral-300 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selectedId && <TicketDetail ticketId={selectedId} onClose={() => setSelectedId(null)} />}

      {/* Create modal */}
      {showCreate && (
        <CreateTicketModal
          onClose={() => setShowCreate(false)}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['tickets'] })}
        />
      )}
    </div>
  );
}
